import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider,
  IconButton,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  Fade,
  Zoom,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import attackApiService from '../../services/alerts/AttackApiService';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const Attacks = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [agent, setAgent] = useState(null);
  const [attackData, setAttackData] = useState(null);
  const [rawAttacks, setRawAttacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAttacks, setLoadingAttacks] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState(searchParams.get('severity') || 'all');
  const [sortBy, setSortBy] = useState('timestamp');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedAttack, setExpandedAttack] = useState(null);

  const fetchAgentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const agentData = await dashboardApiService.getAgentById(id);
      setAgent(agentData);
    } catch (err) {
      setError(`Failed to fetch agent details: ${err.message}`);
      console.error('Agent details fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAttackData = useCallback(async () => {
    try {
      setLoadingAttacks(true);
      const attackResponse = await attackApiService.getAttackAlerts(id);
      const attacks = attackResponse.alerts || attackResponse || [];
      const processedData = attackApiService.processAttackData(attacks);
      
      setRawAttacks(attacks);
      setAttackData(processedData);
    } catch (err) {
      console.error('Attack data fetch error:', err);
    } finally {
      setLoadingAttacks(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchAgentDetails();
      fetchAttackData();
    }
  }, [id, fetchAgentDetails, fetchAttackData]);

  useEffect(() => {
    // Update severity filter when URL parameter changes
    const urlSeverity = searchParams.get('severity');
    if (urlSeverity && urlSeverity !== severityFilter) {
      setSeverityFilter(urlSeverity);
    }
  }, [searchParams, severityFilter]);

  const getFilteredAndSortedAttacks = () => {
    if (!rawAttacks || rawAttacks.length === 0) return [];
    
    let filtered = rawAttacks.filter(attack => {
      const matchesSearch = attack.rule?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           attack.data?.srcip?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           attack.srcip?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (severityFilter === 'all') return matchesSearch;
      
      const attackSeverity = attackApiService.categorizeAttackSeverity(attack.rule?.level || 0).toLowerCase();
      return matchesSearch && attackSeverity === severityFilter.toLowerCase();
    });
    
    // Sort attacks
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'timestamp':
          return new Date(b.timestamp || b['@timestamp'] || 0) - new Date(a.timestamp || a['@timestamp'] || 0);
        case 'severity':
          return (b.rule?.level || 0) - (a.rule?.level || 0);
        case 'source':
          return (a.data?.srcip || a.srcip || '').localeCompare(b.data?.srcip || b.srcip || '');
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#fbc02d';
      case 'low': return '#388e3c';
      default: return '#757575';
    }
  };

  const getSeverityStats = () => {
    if (!attackData) return { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    return attackData.severityStats;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSeverityCardClick = (severity) => {
    setSeverityFilter(severity);
    setPage(0);
    // Update URL parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (severity === 'all') {
      newSearchParams.delete('severity');
    } else {
      newSearchParams.set('severity', severity);
    }
    navigate(`/agents/${id}/attacks?${newSearchParams.toString()}`, { replace: true });
  };

  const refreshData = () => {
    fetchAgentDetails();
    fetchAttackData();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading agent details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={refreshData}
          startIcon={<RefreshIcon />}
          sx={{ mr: 2 }}
        >
          Retry
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => navigate(`/agents/${id}`)}
          startIcon={<ArrowBackIcon />}
        >
          Back to Agent Details
        </Button>
      </Box>
    );
  }

  if (!agent) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Agent not found</Typography>
        <Typography variant="body2">The requested agent could not be found.</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/agents')}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Agents
        </Button>
      </Box>
    );
  }

  const filteredAttacks = getFilteredAndSortedAttacks();
  const paginatedAttacks = filteredAttacks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const stats = getSeverityStats();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Breadcrumbs */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Breadcrumbs>
          <Link 
            color="inherit" 
            onClick={() => navigate('/agents')}
            sx={{ cursor: 'pointer' }}
          >
            Agents
          </Link>
          <Link 
            color="inherit" 
            onClick={() => navigate(`/agents/${id}`)}
            sx={{ cursor: 'pointer' }}
          >
            {agent.name}
          </Link>
          <Typography color="text.primary">Attack Alerts</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            onClick={() => navigate(`/agents/${id}`)}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton 
            onClick={refreshData}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Agent Info Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#ff5722', width: 48, height: 48 }}>
              <ShieldIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Attack Analysis - {agent.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Agent ID: {agent.id} • {agent.ip} • {agent.os.name} {agent.os.version}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip label={`${attackData?.totalAlerts || 0} Total Attacks`} color="error" />
              <Chip 
                label={`${stats.critical + stats.high} Critical & High`} 
                sx={{ bgcolor: '#ffebee', color: '#d32f2f' }} 
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Severity Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'all' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('all')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {attackData?.totalAlerts || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All Attacks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'critical' ? '2px solid #d32f2f' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('critical')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                {stats.critical}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Critical
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'high' ? '2px solid #f57c00' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('high')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                {stats.high}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                High
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'medium' ? '2px solid #fbc02d' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('medium')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#fbc02d', fontWeight: 'bold' }}>
                {stats.medium}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Medium
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'low' ? '2px solid #388e3c' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('low')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#388e3c', fontWeight: 'bold' }}>
                {stats.low}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Low
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search attacks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity Filter</InputLabel>
                <Select
                  value={severityFilter}
                  onChange={(e) => handleSeverityCardClick(e.target.value)}
                  label="Severity Filter"
                  startAdornment={<FilterListIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="all">All Severities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                  startAdornment={<SortIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="timestamp">Timestamp</MenuItem>
                  <MenuItem value="severity">Severity</MenuItem>
                  <MenuItem value="source">Source IP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                {filteredAttacks.length} attacks found
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Attacks Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loadingAttacks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading attacks...</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Severity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Source IP</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Rule ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedAttacks.map((attack, index) => {
                      const severity = attackApiService.categorizeAttackSeverity(attack.rule?.level || 0);
                      const isExpanded = expandedAttack === index;
                      
                      return (
                        <React.Fragment key={index}>
                          <TableRow hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {formatTimestamp(attack.timestamp || attack['@timestamp'])}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={severity}
                                size="small"
                                sx={{
                                  bgcolor: getSeverityColor(severity) + '20',
                                  color: getSeverityColor(severity),
                                  fontWeight: 600,
                                  border: `1px solid ${getSeverityColor(severity)}40`
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  maxWidth: 300,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={attack.rule?.description}
                              >
                                {attack.rule?.description || 'No description available'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {attack.data?.srcip || attack.srcip || 'Unknown'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {attack.rule?.id || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="View Details">
                                <IconButton 
                                  size="small"
                                  onClick={() => setExpandedAttack(isExpanded ? null : index)}
                                  sx={{ 
                                    color: isExpanded ? '#1976d2' : 'text.secondary',
                                    '&:hover': { backgroundColor: '#e3f2fd' }
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded Details Row */}
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={6} sx={{ bgcolor: '#f8f9fa', p: 0 }}>
                                <Box sx={{ p: 3 }}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                        Rule Information
                                      </Typography>
                                      <Box sx={{ pl: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Rule ID:</strong> {attack.rule?.id || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Level:</strong> {attack.rule?.level || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Groups:</strong> {attack.rule?.groups?.join(', ') || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Description:</strong> {attack.rule?.description || 'N/A'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                        Attack Details
                                      </Typography>
                                      <Box sx={{ pl: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Source IP:</strong> {attack.data?.srcip || attack.srcip || 'Unknown'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Destination IP:</strong> {attack.data?.dstip || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Protocol:</strong> {attack.data?.protocol || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Location:</strong> {attack.location || 'N/A'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    
                                    {attack.full_log && (
                                      <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                          Full Log
                                        </Typography>
                                        <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              fontFamily: 'monospace',
                                              fontSize: '12px',
                                              whiteSpace: 'pre-wrap',
                                              wordBreak: 'break-all'
                                            }}
                                          >
                                            {attack.full_log}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredAttacks.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Attacks;
